import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pencil, Archive, MapPin, Wrench, AlertTriangle, Images, Paperclip,
  ChevronRight, Plus, X, Trash2, ClipboardList, Copy, History, Scroll, Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUpdateAsset, useArchiveAsset, useAssets, useChildAssets, useDuplicateAsset } from "@/hooks/useAssets";
import { useAllZonesFlat } from "@/hooks/useFacilityZones";
import type { Asset, AssetType, AssetCriticality, AssetStatus } from "@/hooks/useAssets";
import { usePmPrograms, useAssetPmProgramIds, useSetAssetPmPrograms } from "@/hooks/usePmPrograms";
import { useAssetServiceLog, useAddServiceLogEntry, useDeleteServiceLogEntry } from "@/hooks/useAssetServiceLog";
import { useServiceLogFiles, useUploadEntityFile, useDeleteEntityFile, fileIcon } from "@/hooks/useEntityFiles";
import { Thumbnail } from "@/components/shared/Thumbnail";
import { ImageGallery } from "@/components/shared/ImageGallery";
import { FileAttachments } from "@/components/shared/FileAttachments";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  online: "bg-green-100 text-green-800",
  offline: "bg-gray-100 text-gray-800",
  maintenance: "bg-yellow-100 text-yellow-800",
  decommissioned: "bg-red-100 text-red-800",
};

const ASSET_TYPES: { value: AssetType; label: string }[] = [
  { value: "bottling", label: "Bottling" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "safety", label: "Safety" },
  { value: "wine_production", label: "Wine Production" },
  { value: "vehicle", label: "Vehicle" },
  { value: "tools", label: "Tools" },
  { value: "fluids", label: "Fluids" },
  { value: "tank", label: "Tank" },
];

// ── Sub-assets ─────────────────────────────────────────────────────────────

function SubAssets({ assetId }: { assetId: string }) {
  const { data: children = [] } = useChildAssets(assetId);
  const { data: allAssets = [] } = useAssets();
  const updateAsset = useUpdateAsset();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const childIds = new Set(children.map((c) => c.id));
  const available = allAssets.filter((a) => a.id !== assetId && !childIds.has(a.id) && a.parent_id !== assetId);
  const filtered = available.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));

  const handleAttach = (childId: string) => {
    updateAsset.mutate({ id: childId, input: { parent_id: assetId } }, {
      onSuccess: () => {
        setSearch("");
        setShowDropdown(false);
        setTimeout(() => { searchRef.current?.focus(); setShowDropdown(true); }, 50);
      },
    });
  };

  const handleDetach = (childId: string) => {
    updateAsset.mutate({ id: childId, input: { parent_id: null } });
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        Sub-assets ({children.length})
      </p>

      {children.length > 0 && (
        <div className="rounded-lg border overflow-hidden mb-2">
          {children.map((child) => (
            <div key={child.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 group">
              <Thumbnail url={child.image_url} size={72} className="h-7 w-7 rounded object-cover border border-border shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{child.name}</p>
                {child.asset_type && (
                  <p className="text-xs text-muted-foreground capitalize">{child.asset_type.replace("_", " ")}</p>
                )}
              </div>
              <button
                onClick={() => handleDetach(child.id)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {children.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No sub-assets yet.
        </p>
      )}

      {!adding && (
        <button
          onClick={() => { setAdding(true); setShowDropdown(true); }}
          disabled={available.length === 0}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 disabled:opacity-40"
        >
          <Plus className="h-3 w-3" />
          {available.length === 0 ? "No assets available" : "Add sub-asset"}
        </button>
      )}

      {adding && (
        <div className="rounded-lg border bg-muted/30 p-2 space-y-1">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={searchRef}
                autoFocus
                placeholder="Search assets…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                className="h-8 text-sm"
              />
              {showDropdown && (
                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">No assets found</p>
                  ) : (
                    filtered.map((a) => (
                      <button
                        key={a.id}
                        onMouseDown={(e) => { e.preventDefault(); handleAttach(a.id); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors truncate"
                      >
                        {a.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button onClick={() => { setAdding(false); setSearch(""); setShowDropdown(false); }} className="text-muted-foreground hover:text-foreground shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground px-1">Click an asset to add it as a sub-asset.</p>
        </div>
      )}
    </div>
  );
}

// ── Service History ─────────────────────────────────────────────────────────

function ServiceHistory({ assetId, orgId }: { assetId: string; orgId: string }) {
  const qc = useQueryClient();
  const { data: entries = [] } = useAssetServiceLog(assetId);
  const { data: filesByEntryId = {} } = useServiceLogFiles(assetId);
  const addEntry = useAddServiceLogEntry();
  const deleteEntry = useDeleteServiceLogEntry();
  const uploadFile = useUploadEntityFile("asset");
  const deleteFile = useDeleteEntityFile("asset");

  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [performedAt, setPerformedAt] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const newEntryFileRef = useRef<HTMLInputElement>(null);
  const existingEntryFileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleAdd = () => {
    if (!note.trim()) return;
    addEntry.mutate(
      { assetId, note: note.trim(), performedBy: performedBy.trim() || "—", performedAt },
      {
        onSuccess: (newEntry) => {
          stagedFiles.forEach((file) =>
            uploadFile.mutate({ file, entityId: assetId, orgId, serviceLogId: newEntry.id })
          );
          setNote("");
          setPerformedBy("");
          setPerformedAt(new Date().toISOString().slice(0, 10));
          setStagedFiles([]);
          setAdding(false);
        },
      }
    );
  };

  const handleStageFiles = (fl: FileList | null) => {
    if (!fl) return;
    const accepted = Array.from(fl).filter((f) =>
      ["application/pdf", "image/jpeg", "image/png"].includes(f.type)
    );
    setStagedFiles((prev) => [...prev, ...accepted]);
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <Scroll className="h-4 w-4 text-muted-foreground" />
        Service History
      </p>

      {entries.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No service entries yet.
        </p>
      ) : entries.length > 0 ? (
        <div className="rounded-lg border overflow-hidden mb-2 text-sm">
          {entries.map((e) => {
            const entryFiles = filesByEntryId[e.id] || [];
            return (
              <div key={e.id} className="border-b last:border-b-0">
                <div className="flex items-start gap-2 px-3 py-2.5 group">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{e.note}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {e.performed_by && e.performed_by !== "—" && `${e.performed_by} · `}
                      {format(new Date(e.performed_at), "MMM d, yyyy")}
                    </p>
                    {entryFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {entryFiles.map((f) => {
                          const isImage = f.mime_type.startsWith("image/");
                          return (
                            <a
                              key={f.id}
                              href={f.public_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group/file relative flex flex-col items-center gap-1 rounded-md border bg-muted/40 p-1 hover:bg-muted transition-colors"
                              style={{ width: 64 }}
                            >
                              {isImage ? (
                                <img
                                  src={f.public_url}
                                  alt={f.filename}
                                  className="h-10 w-14 object-cover rounded"
                                />
                              ) : (
                                <div className="h-10 w-14 flex items-center justify-center rounded bg-red-50 border border-red-100">
                                  <span className="text-xs font-bold text-red-500 uppercase">PDF</span>
                                </div>
                              )}
                              <span className="text-[10px] text-muted-foreground w-full truncate text-center leading-tight">
                                {f.filename}
                              </span>
                              <button
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  ev.stopPropagation();
                                  deleteFile.mutate(
                                    { file: f, entityId: assetId },
                                    { onSuccess: () => qc.invalidateQueries({ queryKey: ["service-log-files", assetId] }) }
                                  );
                                }}
                                className="absolute -top-1.5 -right-1.5 opacity-0 group-hover/file:opacity-100 bg-background border rounded-full p-0.5 text-muted-foreground hover:text-destructive transition-opacity"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    <button
                      onClick={() => existingEntryFileRefs.current[e.id]?.click()}
                      className="opacity-0 group-hover:opacity-100 text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5 mt-1 transition-opacity"
                    >
                      <Paperclip className="h-3 w-3" />
                      Attach file
                    </button>
                    <input
                      ref={(el) => { existingEntryFileRefs.current[e.id] = el; }}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(ev) => {
                        if (!ev.target.files) return;
                        Array.from(ev.target.files).forEach((file) =>
                          uploadFile.mutate({ file, entityId: assetId, orgId, serviceLogId: e.id })
                        );
                        ev.target.value = "";
                      }}
                    />
                  </div>
                  <button
                    onClick={() => deleteEntry.mutate({ id: e.id, assetId })}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0 mt-0.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
        >
          <Plus className="h-3 w-3" />
          Add entry
        </button>
      ) : (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
          <Textarea
            autoFocus
            placeholder="What was done? e.g. Replaced drive belt, lubed bearings"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="text-sm resize-none border-gray-300"
          />
          <div className="flex items-center gap-2">
            <Input
              placeholder="Performed by"
              value={performedBy}
              onChange={(e) => setPerformedBy(e.target.value)}
              className="h-8 text-sm flex-1 border-gray-300"
            />
            <Input
              type="date"
              value={performedAt}
              onChange={(e) => setPerformedAt(e.target.value)}
              className="h-8 text-sm w-36 border-gray-300"
            />
          </div>
          <div>
            {stagedFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {stagedFiles.map((f, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1 text-xs bg-muted rounded px-1.5 py-0.5"
                  >
                    <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="max-w-[110px] truncate">{f.name}</span>
                    <button
                      onClick={() => setStagedFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false); }}
              onDrop={(e) => { e.preventDefault(); setIsDraggingOver(false); handleStageFiles(e.dataTransfer.files); }}
              onClick={() => newEntryFileRef.current?.click()}
              className={cn(
                "flex items-center justify-center gap-2 rounded-md border-2 border-dashed py-3 cursor-pointer transition-colors select-none",
                isDraggingOver
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-gray-300 text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
              )}
            >
              <Upload className="h-4 w-4 shrink-0" />
              <p className="text-xs">
                {isDraggingOver ? "Drop to attach" : "Drag & drop or click to attach (PDF, JPG, PNG)"}
              </p>
            </div>
            <input
              ref={newEntryFileRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => { handleStageFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={handleAdd} disabled={!note.trim() || addEntry.isPending}>
              {addEntry.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setAdding(false); setNote(""); setPerformedBy(""); setStagedFiles([]); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

interface AssetDetailSheetProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetUpdated?: (asset: Asset) => void;
}

export function AssetDetailSheet({ asset: assetProp, open, onOpenChange, onAssetUpdated }: AssetDetailSheetProps) {
  const [displayAsset, setDisplayAsset] = useState<Asset | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  // Edit state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assetType, setAssetType] = useState("");
  const [status, setStatus] = useState("");
  const [criticality, setCriticality] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");

  const [selectedPmIds, setSelectedPmIds] = useState<string[]>([]);

  const updateAsset = useUpdateAsset();
  const archiveAsset = useArchiveAsset();
  const duplicateAsset = useDuplicateAsset();
  const setAssetPmPrograms = useSetAssetPmPrograms();
  const zones = useAllZonesFlat();
  const { data: allPrograms = [] } = usePmPrograms();
  const { data: assetPmIds = [] } = useAssetPmProgramIds(displayAsset?.id ?? null);

  useEffect(() => {
    setDisplayAsset(assetProp);
    setIsEditing(false);
  }, [assetProp]);

  const handleEdit = () => {
    if (!displayAsset) return;
    setName(displayAsset.name);
    setDescription(displayAsset.description || "");
    setAssetType(displayAsset.asset_type || "");
    setStatus(displayAsset.status);
    setCriticality(displayAsset.criticality);
    setZoneId(displayAsset.zone_id || "");
    setMake(displayAsset.make || "");
    setModel(displayAsset.model || "");
    setSerialNumber(displayAsset.serial_number || "");
    setSelectedPmIds(assetPmIds);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!displayAsset) return;
    updateAsset.mutate(
      {
        id: displayAsset.id,
        input: {
          name,
          description: description || null,
          asset_type: (assetType as AssetType) || null,
          status: (status as AssetStatus) || undefined,
          criticality: (criticality as AssetCriticality) || undefined,
          facility_id: zones.find((z) => z.id === zoneId)?.facility_id || null,
          zone_id: zoneId || null,
          make: make || null,
          model: model || null,
          serial_number: serialNumber || null,
        },
      },
      {
        onSuccess: (data) => {
          setDisplayAsset(data);
          onAssetUpdated?.(data);
          setAssetPmPrograms.mutate({ assetId: data.id, programIds: selectedPmIds });
          setIsEditing(false);
        },
      }
    );
  };

  const handleArchive = () => {
    if (!displayAsset) return;
    archiveAsset.mutate(displayAsset.id, {
      onSuccess: () => onOpenChange(false),
    });
  };

  if (!displayAsset) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-xl leading-tight">{displayAsset.name}</SheetTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={STATUS_COLORS[displayAsset.status] || ""}>
                {displayAsset.status}
              </Badge>
              {displayAsset.criticality === "critical" && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Critical
                </Badge>
              )}
              {displayAsset.asset_type && (
                <Badge variant="outline">{displayAsset.asset_type.replace("_", " ")}</Badge>
              )}
              {!isEditing && (
                <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1 ml-auto" onClick={handleEdit}>
                  <Pencil className="h-3 w-3" />Edit
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {!isEditing ? (
              <>
                {/* Photos — top of sheet */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Images className="h-3.5 w-3.5" />
                    Photos
                  </p>
                  <ImageGallery
                    entityType="asset"
                    entityId={displayAsset.id}
                    orgId={displayAsset.org_id}
                  />
                </div>

                {/* Files & Manuals — grouped with photos as reference material */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    Files & Manuals
                  </p>
                  <FileAttachments
                    entityType="asset"
                    entityId={displayAsset.id}
                    orgId={displayAsset.org_id}
                  />
                </div>

                <Separator />

                {/* Info block */}
                <div className="space-y-2">
                  {displayAsset.description && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{displayAsset.description}</p>
                  )}
                  {displayAsset.parent_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Part of </span>
                      <span className="font-medium">{displayAsset.parent_name}</span>
                    </div>
                  )}
                  {displayAsset.zone_id && (() => {
                    const z = zones.find((z) => z.id === displayAsset.zone_id);
                    return z ? (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>{z.label}</span>
                      </div>
                    ) : null;
                  })()}
                  {(displayAsset.make || displayAsset.model) && (
                    <div className="flex items-center gap-2 text-sm">
                      <Wrench className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span>{[displayAsset.make, displayAsset.model].filter(Boolean).join(" ")}</span>
                    </div>
                  )}
                  {displayAsset.serial_number && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Serial: </span>
                      <span className="font-mono">{displayAsset.serial_number}</span>
                    </div>
                  )}
                  {/* PM Programs inline */}
                  {assetPmIds.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                      <ClipboardList className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground shrink-0">PM:</span>
                      {assetPmIds.map((id) => {
                        const prog = allPrograms.find((p) => p.id === id);
                        return prog ? (
                          <Badge key={id} variant="secondary" className="text-xs">{prog.name}</Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* Sub-assets */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <SubAssets assetId={displayAsset.id} />
                </div>

                {/* Service History */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <ServiceHistory assetId={displayAsset.id} orgId={displayAsset.org_id} />
                </div>

                {/* Traceability */}
                {(displayAsset.updated_by_name || displayAsset.updated_at) && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <History className="h-3 w-3 shrink-0" />
                    <span>
                      Last updated
                      {displayAsset.updated_by_name ? ` by ${displayAsset.updated_by_name}` : ""}
                      {displayAsset.updated_at
                        ? ` · ${format(new Date(displayAsset.updated_at), "MMM d, yyyy")}`
                        : ""}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground gap-1.5"
                    onClick={() => duplicateAsset.mutate(displayAsset)}
                    disabled={duplicateAsset.isPending}
                  >
                    <Copy className="h-3.5 w-3.5" />New from this
                  </Button>
                  <button
                    onClick={() => setShowArchiveDialog(true)}
                    className="text-xs text-muted-foreground hover:text-destructive transition-colors px-2 py-1"
                  >
                    Archive
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="decommissioned">Decommissioned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={assetType} onValueChange={setAssetType}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {ASSET_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Criticality</Label>
                    <Select value={criticality} onValueChange={setCriticality}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Select value={zoneId || "__none__"} onValueChange={(v) => setZoneId(v === "__none__" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {zones.map((z) => (
                          <SelectItem key={z.id} value={z.id}>{z.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Make</Label>
                    <Input value={make} onChange={(e) => setMake(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Input value={model} onChange={(e) => setModel(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Serial #</Label>
                    <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
                  </div>
                </div>
                {allPrograms.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                      PM Programs
                    </Label>
                    <div className="rounded-lg border p-3 space-y-2">
                      {allPrograms.map((prog) => {
                        const checked = selectedPmIds.includes(prog.id);
                        return (
                          <label key={prog.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setSelectedPmIds((prev) =>
                                  checked ? prev.filter((id) => id !== prog.id) : [...prev, prog.id]
                                )
                              }
                              className="h-4 w-4 rounded border-border"
                            />
                            <span className="text-sm">{prog.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                  <Button onClick={handleSave} disabled={updateAsset.isPending}>
                    {updateAsset.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {displayAsset.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This asset will be hidden from active views. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
